<template>
    <pc-capsule
        v-bind="capsuleBinderOverride"
        has-body
        data-test-role="inquiries-capsule"
        v-on="capsuleListeners">
        <div
            v-if="threads.length <= 0"
            class="fst-italic w-100">
            {{ noInquiriesLabel }}
        </div>
        <div v-else>
            <div>
                <pc-select
                    v-model="currentThreadId"
                    v-bind:label="selectInquiryLabel"
                    v-bind:optgroups="threadGroups"
                    text-prop="FriendlyName"
                    value-prop="ThreadId" />
            </div>
            <hr class="pc-capsule-hr" />
            <inquiry-thread
                v-bind:program-id="ProgramId"
                v-bind:project-id="ProjectId"
                v-bind:thread-id="currentThreadId"
                v-bind:thread-closed="threadClosed"
                v-bind:thread-status="threadStatus"
                v-bind:notes="notes"
                v-bind:active-statuses="statuses"
                v-bind:sms-features-enabled="smsFeaturesEnabled"
                v-bind:sms-notification-status="currentSmsNotificationStatus"
                v-on:inq-save-note="getNotesForThread"
                v-on:inq-set-status="refreshThreads" />
        </div>
    </pc-capsule>
</template>
<script>
    import capsuleBase from "Projects/SharedCapsules/_sharedCapsuleBase";
    import inquiryThread from "Inquiries/inquiry-thread.vue";
    import { KEYS, NAMESPACE } from "./i18n/projectInquiriesCapsule.i18n";
    import { mapI18n } from "Util/i18n";
    import { GlobalEventBus } from "Util/emitter";

    export default {
        components: {
            inquiryThread
        },
        extends: capsuleBase,
        props: {
            id: { type: String, required: false, default: "project-inquiries" },
            model: { type: Object, required: true }
        },
        data() {
            return {
                threads: [],
                currentThreadId: "",
                statuses: [],
                notes: [],
                smsFeaturesEnabled: false
            };
        },
        computed: {
            ...mapI18n({ ...KEYS }, NAMESPACE),
            capsuleBinderOverride() {
                return {
                    ...this.capsuleBinder,
                    title: this.calcTitle
                };
            },
            calcTitle() {
                return this.$app.res({
                    namespace: NAMESPACE,
                    key: KEYS.projectInquiriesCapsuleTitle,
                    values: { buttonText: this.$serviceControllers.ProjectInquiriesController.SupportInfo.ButtonText }
                });
            },
            threadGroups() {
                const groups = [
                    { name: this.openThreadsLabel, options: this.threads.filter((t) => t && !t.Closed) },
                    { name: this.closedThreadsLabel, options: this.threads.filter((t) => t && t.Closed) }
                ];
                return groups.filter((g) => g.options.length > 0);
            },
            threadClosed() {
                return this.currentThreadId && this.getCurrentThread().Closed;
            },
            threadStatus() {
                return this.currentThreadId ? this.getCurrentThread().Status : null;
            },
            selectedThreadCOLSKey() {
                return `cols_pc_${this.currentUserId}_features_selectedinquirythread`;
            },
            currentSmsNotificationStatus() {
                return this.currentThreadId ? this.getCurrentThread().SmsNotificationsEnabledForCurrentUser : false;
            }
        },
        watch: {
            currentThreadId() {
                this.getNotesForThread();
                this.updateSelectedThread();
            }
        },
        created() {
            this.getStatuses();
            this.getThreads();
        },
        mounted() {
            GlobalEventBus.on("inquiry_dialog_closed", () => {
                this.refreshThreads();
            });
        },
        methods: {
            async getStatuses() {
                const result = await this.$serviceControllers.ProjectInquiriesController.GetActiveStatuses({
                    programId: this.ProgramId
                });
                this.statuses = result.Data.Statuses;
                this.smsFeaturesEnabled = result.Data.SmsFeaturesEnabled;
            },
            async getThreads(index) {
                const result = await this.$serviceControllers.ProjectInquiries.GetInquiryThreads({
                    programId: this.ProgramId,
                    projectId: this.ProjectId
                });
                if (result.status === "success") {
                    this.threads = result.Data.Threads.sort(function (a, b) {
                        return a.Index - b.Index;
                    });
                    if (this.threads.length > 0) {
                        let defaultIndex = this.getFirstOpenThreadIndex();
                        if (defaultIndex < 0) {
                            defaultIndex = 0;
                        }
                        if (!index || index < 0) {
                            const selectedThreadId = await this.$app.getCOLS_item(this.selectedThreadCOLSKey);
                            if (selectedThreadId) {
                                index = this.getThreadIndex(selectedThreadId);
                                if (index < 0 || this.threads[index].Closed) {
                                    index = defaultIndex;
                                }
                            } else index = defaultIndex;
                        }
                        this.currentThreadId = this.threads[index].ThreadId;
                    }
                }
            },
            async getNotesForThread() {
                if (!this.currentThreadId) {
                    return;
                }
                const result = await this.$serviceControllers.ProjectInquiries.GetInquiryNotes({
                    programId: this.ProgramId,
                    projectId: this.ProjectId,
                    threadId: this.currentThreadId
                });
                if (result.status === "success") {
                    this.notes = result.Data.Notes.sort(function (a, b) {
                        return a.CreatedTicks - b.CreatedTicks;
                    });
                }
            },
            refreshThreads() {
                const index = this.getThreadIndex(this.currentThreadId);
                this.getThreads(index);
                this.getNotesForThread();
            },
            getFirstOpenThreadIndex() {
                return this.threads.findIndex((t) => !t.Closed);
            },
            getThreadIndex(threadId) {
                return this.threads
                    .map(function (t) {
                        return t.ThreadId;
                    })
                    .indexOf(threadId);
            },
            getCurrentThread() {
                const cti = this.currentThreadId;
                return this.threads.find((t) => t.ThreadId === cti);
            },
            updateSelectedThread() {
                if (!this.currentThreadId) {
                    return;
                }
                this.$app.setCOLS_item(this.selectedThreadCOLSKey, this.currentThreadId);
            }
        }
    };
</script>
<style lang="scss" scoped>
    div.inquiry-conversation {
        max-height: 30rem;
        overflow-y: auto;
    }

    div.project-note-user {
        border: 1px solid var(--bs-gray-600);
        background-color: var(--bs-primary-100);
    }

    div.project-note-admin {
        border: 1px solid var(--bs-gray-600);
        background-color: var(--bs-primary-200);
    }

    div.project-note-content {
        white-space: pre-wrap;
    }
</style>
